import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelectedBusinessId } from '../hooks/useSelectedBusiness';
import {
  Copy,
  Trash2,
  Edit2,
  Check,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Send,
  Download,
  ExternalLink,
  Upload,
} from 'lucide-react';
import {
  API_URL,
  contentApi,
  ContentItem,
  ContentItemType,
  Business,
  GenerateContentInput,
  PublicationDraftResult,
  PromotionDraftResult,
} from '../lib/api';
import { getBusinessList } from '../lib/businessListCache';
import { PageHeader, Card, Button, Input } from '../components/ui';

const MAX_PROMOTION_REFERENCES = 6;

interface PromotionReferenceFile {
  rawUrl: string;
  previewUrl: string;
  originalFilename: string;
}

const fileToDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const value = reader.result;
    if (typeof value !== 'string') {
      reject(new Error('Не удалось прочитать файл'));
      return;
    }
    resolve(value);
  };
  reader.onerror = () => reject(new Error('Ошибка чтения файла'));
  reader.readAsDataURL(file);
});

const CONTENT_TYPES: { value: ContentItemType; label: string; description: string; placeholder: string }[] = [
  {
    value: 'ARTICLE',
    label: 'Статья',
    description: 'Длинный текст для блога или раздела «О нас»',
    placeholder: 'Например: Напиши статью о преимуществах стоматологии нашей клиники для семей с детьми',
  },
  {
    value: 'HEADLINE',
    label: 'Заголовок',
    description: 'Цепляющий заголовок для карточки или акции',
    placeholder: 'Например: Заголовок для карточки автосервиса, делаем кузовной ремонт за 3 дня',
  },
  {
    value: 'PROMOTION',
    label: 'Акция',
    description: 'Текст акции или специального предложения',
    placeholder: 'Например: Скидка 20% на первое посещение в январе, для фитнес-клуба',
  },
  {
    value: 'NEWS',
    label: 'Новость',
    description: 'Короткая новость для ленты событий',
    placeholder: 'Например: Объявление об открытии нового зала на Пресненской набережной',
  },
];

const extractYandexOrgId = (business?: Business): string | null => {
  if (!business) return null;

  const explicit = business.yandexOrgId?.trim();
  if (explicit) return explicit;

  const sourceUrl = business.yandexMapsUrl?.trim();
  if (!sourceUrl) return null;

  const matchers = [
    /\/sprav\/(\d+)/i,
    /\/org\/[^/]+\/(\d+)/i,
    /[/?&]oid=(\d+)/i,
    /\/(\d+)(?:\/|\?|$)/,
  ];

  for (const matcher of matchers) {
    const match = sourceUrl.match(matcher);
    if (match?.[1]) {
      return match[1];
    }
  }

  return null;
};

const resolveYandexPostsUrl = (business?: Business): string | null => {
  const orgId = extractYandexOrgId(business);
  if (!orgId) return null;
  return `https://yandex.ru/sprav/${orgId}/p/edit/posts/`;
};

const resolveWebsiteUrl = (websiteUrl?: string): string | null => {
  if (!websiteUrl) return null;
  const trimmed = websiteUrl.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `https://${trimmed}`;
};

export function Content() {
  const [searchParams] = useSearchParams();
  const selectedBusinessId = useSelectedBusinessId() ?? '';
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [activeType, setActiveType] = useState<ContentItemType>('ARTICLE');
  const [items, setItems] = useState<ContentItem[]>([]);
  const [brief, setBrief] = useState('');
  const [keywords, setKeywords] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [publishFeedback, setPublishFeedback] = useState<Record<string, {
    type: 'success' | 'warning' | 'error';
    message: string;
    creativeUrl?: string | null;
  }>>({});
  const [topics, setTopics] = useState<string[]>([]);
  const [isSuggestingTopics, setIsSuggestingTopics] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [customTopic, setCustomTopic] = useState('');
  const [isGeneratingPublication, setIsGeneratingPublication] = useState(false);
  const [publicationDraft, setPublicationDraft] = useState<PublicationDraftResult | null>(null);
  const [copiedPublicationPart, setCopiedPublicationPart] = useState<'title' | 'text' | 'all' | null>(null);
  const [promotionTask, setPromotionTask] = useState('');
  const [isGeneratingPromotion, setIsGeneratingPromotion] = useState(false);
  const [promotionDraft, setPromotionDraft] = useState<PromotionDraftResult | null>(null);
  const [copiedPromotionPart, setCopiedPromotionPart] = useState<'title' | 'text' | 'all' | null>(null);
  const [promotionVariantsCount, setPromotionVariantsCount] = useState(3);
  const [promotionReferences, setPromotionReferences] = useState<PromotionReferenceFile[]>([]);
  const [isUploadingPromotionRefs, setIsUploadingPromotionRefs] = useState(false);
  const promotionRefsInputRef = useRef<HTMLInputElement | null>(null);

  const queryFocus = searchParams.get('focus')?.trim() || '';

  useEffect(() => {
    getBusinessList().then(setBusinesses).catch(() => {});
  }, []);

  useEffect(() => {
    if (queryFocus === 'publication') {
      setActiveType('ARTICLE');
    }
  }, [queryFocus]);

  useEffect(() => {
    if (!selectedBusinessId) return;
    setIsLoading(true);
    contentApi.getByBusiness(selectedBusinessId, activeType)
      .then((res) => setItems(res.data.data))
      .catch(() => setError('Не удалось загрузить контент'))
      .finally(() => setIsLoading(false));
  }, [selectedBusinessId, activeType]);

  useEffect(() => {
    setTopics([]);
    setSelectedTopic('');
    setCustomTopic('');
    setPublicationDraft(null);
    setPromotionTask('');
    setPromotionDraft(null);
    setPromotionVariantsCount(3);
    setPromotionReferences([]);
  }, [selectedBusinessId]);

  const handleGenerate = async () => {
    if (!brief.trim()) { setError('Заполните бриф'); return; }
    setIsGenerating(true);
    setError('');
    try {
      const input: GenerateContentInput = {
        businessId: selectedBusinessId,
        type: activeType,
        brief: brief.trim(),
        keywords: keywords.trim()
          ? keywords.split(',').map((k) => k.trim()).filter(Boolean)
          : undefined,
      };
      const res = await contentApi.generate(input);
      setItems((prev) => [res.data.data, ...prev]);
      setBrief('');
      setKeywords('');
      setExpandedId(res.data.data.id);
    } catch {
      setError('Ошибка генерации. Проверьте настройки AI-сервиса.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSuggestTopics = async () => {
    if (!selectedBusinessId) {
      setError('Сначала выберите бизнес');
      return;
    }

    setIsSuggestingTopics(true);
    setError('');

    try {
      const res = await contentApi.suggestPublicationTopics({
        businessId: selectedBusinessId,
        count: 8,
      });
      const suggestedTopics = res.data.data;
      setTopics(suggestedTopics);
      if (suggestedTopics.length > 0) {
        setSelectedTopic(suggestedTopics[0]);
      }
    } catch {
      setError('Не удалось подобрать темы публикаций');
    } finally {
      setIsSuggestingTopics(false);
    }
  };

  const handleGeneratePublication = async () => {
    if (!selectedBusinessId) {
      setError('Сначала выберите бизнес');
      return;
    }

    const topic = customTopic.trim() || selectedTopic.trim();
    if (!topic) {
      setError('Выберите тему из списка или введите свою');
      return;
    }

    setIsGeneratingPublication(true);
    setError('');

    try {
      const res = await contentApi.generatePublicationDraft({
        businessId: selectedBusinessId,
        topic,
      });
      const draft = res.data.data;
      setPublicationDraft({
        ...draft,
        creativeUrl: resolveCreativeUrl(draft.creativeUrl),
      });
      setItems((prev) => [draft.contentItem, ...prev.filter((item) => item.id !== draft.contentItem.id)]);
      setExpandedId(draft.contentItem.id);
    } catch {
      setError('Не удалось сгенерировать публикацию');
    } finally {
      setIsGeneratingPublication(false);
    }
  };

  const handleGeneratePromotion = async () => {
    if (!selectedBusinessId) {
      setError('Сначала выберите бизнес');
      return;
    }

    const normalizedTask = promotionTask.trim();
    if (!normalizedTask) {
      setError('Введите ТЗ для акции');
      return;
    }

    setIsGeneratingPromotion(true);
    setError('');

    try {
      const res = await contentApi.generatePromotionDraft({
        businessId: selectedBusinessId,
        task: normalizedTask,
        variantsCount: promotionVariantsCount,
        referenceImageUrls: promotionReferences.map((reference) => reference.rawUrl),
      });
      const draft = res.data.data;
      const normalizedCreativeUrl = resolveCreativeUrl(draft.creativeUrl);
      const normalizedCreatives = (Array.isArray(draft.creatives) ? draft.creatives : [])
        .map((creative) => ({
          ...creative,
          url: resolveCreativeUrl(creative.url) || creative.url,
        }));

      if (normalizedCreatives.length === 0 && normalizedCreativeUrl) {
        normalizedCreatives.push({
          index: 1,
          url: normalizedCreativeUrl,
        });
      }

      setPromotionDraft({
        ...draft,
        creativeUrl: normalizedCreativeUrl,
        creatives: normalizedCreatives,
      });
      setItems((prev) => [draft.contentItem, ...prev.filter((item) => item.id !== draft.contentItem.id)]);
      setExpandedId(draft.contentItem.id);
    } catch {
      setError('Не удалось сгенерировать акцию');
    } finally {
      setIsGeneratingPromotion(false);
    }
  };

  const handlePromotionReferenceFilesChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!selectedBusinessId) {
      setError('Сначала выберите бизнес');
      event.target.value = '';
      return;
    }

    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    const availableSlots = Math.max(0, MAX_PROMOTION_REFERENCES - promotionReferences.length);
    if (availableSlots === 0) {
      setError(`Можно загрузить максимум ${MAX_PROMOTION_REFERENCES} референсов.`);
      event.target.value = '';
      return;
    }

    const filesToUpload = selectedFiles.slice(0, availableSlots);
    setIsUploadingPromotionRefs(true);
    setError('');

    let uploadedCount = 0;
    let failedCount = 0;
    const uploadedItems: PromotionReferenceFile[] = [];

    for (const file of filesToUpload) {
      try {
        const dataUrl = await fileToDataUrl(file);
        const response = await contentApi.uploadPromotionReference({
          businessId: selectedBusinessId,
          fileName: file.name,
          dataUrl,
        });
        const uploaded = response.data.data;
        uploadedItems.push({
          rawUrl: uploaded.url,
          previewUrl: resolveCreativeUrl(uploaded.url) || uploaded.url,
          originalFilename: uploaded.originalFilename,
        });
        uploadedCount += 1;
      } catch {
        failedCount += 1;
      }
    }

    if (uploadedItems.length > 0) {
      setPromotionReferences((prev) => {
        const combined = [...prev, ...uploadedItems];
        const seen = new Set<string>();
        return combined.filter((item) => {
          if (seen.has(item.rawUrl)) return false;
          seen.add(item.rawUrl);
          return true;
        }).slice(0, MAX_PROMOTION_REFERENCES);
      });
    }

    if (failedCount > 0) {
      setError(`Загружено ${uploadedCount}, с ошибкой: ${failedCount}. Проверьте формат PNG/JPG/WEBP и размер до 10MB.`);
    }

    setIsUploadingPromotionRefs(false);
    event.target.value = '';
  };

  const handleRemovePromotionReference = (rawUrl: string) => {
    setPromotionReferences((prev) => prev.filter((item) => item.rawUrl !== rawUrl));
  };

  const handleCopy = async (item: ContentItem) => {
    await navigator.clipboard.writeText(`${item.title}\n\n${item.content}`);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyPublicationPart = async (part: 'title' | 'text' | 'all') => {
    if (!publicationDraft) return;

    if (part === 'title') {
      await navigator.clipboard.writeText(publicationDraft.contentItem.title);
    } else if (part === 'text') {
      await navigator.clipboard.writeText(publicationDraft.contentItem.content);
    } else {
      await navigator.clipboard.writeText(
        `${publicationDraft.contentItem.title}\n\n${publicationDraft.contentItem.content}`,
      );
    }

    setCopiedPublicationPart(part);
    setTimeout(() => setCopiedPublicationPart(null), 2000);
  };

  const handleCopyPromotionPart = async (part: 'title' | 'text' | 'all') => {
    if (!promotionDraft) return;

    if (part === 'title') {
      await navigator.clipboard.writeText(promotionDraft.contentItem.title);
    } else if (part === 'text') {
      await navigator.clipboard.writeText(promotionDraft.contentItem.content);
    } else {
      await navigator.clipboard.writeText(
        `${promotionDraft.contentItem.title}\n\n${promotionDraft.contentItem.content}`,
      );
    }

    setCopiedPromotionPart(part);
    setTimeout(() => setCopiedPromotionPart(null), 2000);
  };

  const handleEditStart = (item: ContentItem) => {
    setEditingId(item.id);
    setEditTitle(item.title);
    setEditContent(item.content);
  };

  const handleEditSave = async (id: string) => {
    try {
      const res = await contentApi.update(id, { title: editTitle, content: editContent });
      setItems((prev) => prev.map((i) => (i.id === id ? res.data.data : i)));
      setEditingId(null);
    } catch {
      setError('Не удалось сохранить');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить контент?')) return;
    try {
      await contentApi.delete(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setPublishFeedback((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch {
      setError('Не удалось удалить контент');
    }
  };

  const resolveCreativeUrl = (rawUrl?: string | null): string | null => {
    if (!rawUrl) return null;
    if (rawUrl.startsWith('http://') || rawUrl.startsWith('https://')) return rawUrl;

    const origin = API_URL.startsWith('http')
      ? new URL(API_URL).origin
      : window.location.origin;
    const normalizedPath = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
    return `${origin}${normalizedPath}`;
  };

  const handlePublishToYandex = async (item: ContentItem) => {
    setError('');
    setPublishingId(item.id);
    try {
      const res = await contentApi.publishToYandex(item.id);
      const data = res.data.data;
      setPublishFeedback((prev) => ({
        ...prev,
        [item.id]: {
          type: data.delivery.published ? 'success' : 'warning',
          message: data.delivery.message,
          creativeUrl: resolveCreativeUrl(data.creativeUrl),
        },
      }));
    } catch (e) {
      const responseError = (e as { response?: { data?: { error?: string } } }).response?.data?.error;
      setPublishFeedback((prev) => ({
        ...prev,
        [item.id]: {
          type: 'error',
          message: responseError || 'Не удалось отправить публикацию в Яндекс.',
        },
      }));
    } finally {
      setPublishingId(null);
    }
  };

  const activeTypeConfig = CONTENT_TYPES.find((ct) => ct.value === activeType)!;
  const selectedBusiness = businesses.find((business) => business.id === selectedBusinessId);
  const yandexPostsUrl = resolveYandexPostsUrl(selectedBusiness);
  const websiteUrl = resolveWebsiteUrl(selectedBusiness?.websiteUrl);
  const filteredItems = items.filter((i) => i.type === activeType);
  const promotionDraftCreatives = promotionDraft
    ? promotionDraft.creatives.length > 0
      ? promotionDraft.creatives
      : (promotionDraft.creativeUrl
        ? [{ index: 1, url: promotionDraft.creativeUrl }]
        : [])
    : [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Контент и публикации"
        subtitle="Генерация материалов для Яндекс.Карт, публикаций и акций"
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Type tabs */}
      <div className="flex gap-0 border-b border-gray-200">
        {CONTENT_TYPES.map((ct) => (
          <button
            key={ct.value}
            onClick={() => setActiveType(ct.value)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeType === ct.value
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {ct.label}
          </button>
        ))}
      </div>

      {/* Publication workflow */}
      {activeType === 'ARTICLE' && (
        <Card className="space-y-5">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                Публикации для Яндекс Бизнес
              </h2>
              <p className="text-sm text-gray-500">
                Подберите тему, сгенерируйте текст и креатив 400×160, затем вставьте в Яндекс.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={handleSuggestTopics}
                disabled={isSuggestingTopics || !selectedBusinessId}
                loading={isSuggestingTopics}
                icon={!isSuggestingTopics ? <Sparkles className="w-4 h-4" /> : undefined}
              >
                {isSuggestingTopics ? 'Подбираю темы...' : 'Подобрать темы'}
              </Button>
              {yandexPostsUrl && (
                <a
                  href={yandexPostsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Открыть публикации клиента
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {topics.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Варианты тем</p>
              <div className="flex flex-wrap gap-2">
                {topics.map((topic) => (
                  <button
                    key={topic}
                    onClick={() => setSelectedTopic(topic)}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                      selectedTopic === topic
                        ? 'bg-primary-50 border-primary-300 text-primary-700'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {topic}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <Input
              label="Тема публикации"
              type="text"
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              placeholder={selectedTopic || 'Введите свою тему или выберите из предложенных'}
            />

            <Button
              variant="primary"
              size="lg"
              onClick={handleGeneratePublication}
              disabled={isGeneratingPublication || !selectedBusinessId || !(customTopic.trim() || selectedTopic)}
              loading={isGeneratingPublication}
              icon={!isGeneratingPublication ? <Sparkles className="w-4 h-4" /> : undefined}
            >
              {isGeneratingPublication ? 'Генерирую публикацию...' : 'Сгенерировать публикацию'}
            </Button>
          </div>

          {publicationDraft && (
            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-600">
                  Тема: <span className="font-medium text-gray-900">{publicationDraft.topic}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleCopyPublicationPart('title')}>
                    {copiedPublicationPart === 'title' ? 'Заголовок скопирован' : 'Скопировать заголовок'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleCopyPublicationPart('text')}>
                    {copiedPublicationPart === 'text' ? 'Текст скопирован' : 'Скопировать текст'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleCopyPublicationPart('all')}>
                    {copiedPublicationPart === 'all' ? 'Скопировано' : 'Скопировать всё'}
                  </Button>
                </div>
              </div>

              {publicationDraft.creativeUrl ? (
                <div className="space-y-2">
                  <div className="w-full max-w-[400px] aspect-[5/2] overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <img
                      src={publicationDraft.creativeUrl}
                      alt="Креатив для публикации"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <a
                    href={publicationDraft.creativeUrl}
                    download={`publication-${publicationDraft.contentItem.id}.png`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-white transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Скачать креатив
                  </a>
                </div>
              ) : (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Креатив не сгенерирован, но текст публикации готов.
                </p>
              )}

              {publicationDraft.creativeWarning && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {publicationDraft.creativeWarning}
                </p>
              )}

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-3">
                  {publicationDraft.contentItem.title}
                </h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {publicationDraft.contentItem.content}
                </p>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Promotion workflow */}
      {activeType === 'PROMOTION' && (
        <Card className="space-y-5">
          <div className="flex flex-col gap-2">
            <h2 className="text-base font-semibold text-gray-900">
              Акции для клиента
            </h2>
            <p className="text-sm text-gray-500">
              Генерация текста и нескольких креативов 900×480 по ТЗ, сайту клиента и вашим референсам.
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            {websiteUrl ? (
              <p className="text-sm text-gray-700">
                Сайт клиента для ориентира:{' '}
                <a
                  href={websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline break-all"
                >
                  {websiteUrl}
                </a>
              </p>
            ) : (
              <p className="text-sm text-amber-700">
                В карточке клиента не указан сайт. Генерация пройдет только по ТЗ и данным бизнеса.
              </p>
            )}
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-[220px_1fr]">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Количество вариантов
                </label>
                <select
                  value={promotionVariantsCount}
                  onChange={(event) => setPromotionVariantsCount(Number(event.target.value))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {[1, 2, 3, 4, 5, 6].map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Референсы (PNG/JPG/WEBP)
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => promotionRefsInputRef.current?.click()}
                    disabled={isUploadingPromotionRefs || promotionReferences.length >= MAX_PROMOTION_REFERENCES}
                    loading={isUploadingPromotionRefs}
                    icon={!isUploadingPromotionRefs ? <Upload className="w-4 h-4" /> : undefined}
                  >
                    {isUploadingPromotionRefs ? 'Загружаю...' : 'Загрузить примеры'}
                  </Button>
                  <span className="text-xs text-gray-500">
                    Загружено {promotionReferences.length}/{MAX_PROMOTION_REFERENCES}
                  </span>
                </div>
                <input
                  ref={promotionRefsInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  multiple
                  className="hidden"
                  onChange={handlePromotionReferenceFilesChange}
                />
              </div>
            </div>

            {promotionReferences.length > 0 && (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {promotionReferences.map((reference) => (
                  <div key={reference.rawUrl} className="rounded-lg border border-gray-200 bg-white p-2 space-y-2">
                    <div className="aspect-[15/8] overflow-hidden rounded-md border border-gray-100 bg-gray-50">
                      <img
                        src={reference.previewUrl}
                        alt={reference.originalFilename}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs text-gray-600 truncate" title={reference.originalFilename}>
                        {reference.originalFilename}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePromotionReference(reference.rawUrl)}
                        className="px-2 py-1 text-xs"
                      >
                        Удалить
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ТЗ для акции
              </label>
              <textarea
                value={promotionTask}
                onChange={(e) => setPromotionTask(e.target.value)}
                placeholder="Например: Акция на профессиональную чистку, скидка 25% до 31 мая, акцент на семейную аудиторию и быстрый онлайн-запись."
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>

            <Button
              variant="primary"
              size="lg"
              onClick={handleGeneratePromotion}
              disabled={isGeneratingPromotion || isUploadingPromotionRefs || !selectedBusinessId || !promotionTask.trim()}
              loading={isGeneratingPromotion}
              icon={!isGeneratingPromotion ? <Sparkles className="w-4 h-4" /> : undefined}
            >
              {isGeneratingPromotion ? 'Генерирую акцию...' : 'Сгенерировать акцию'}
            </Button>
          </div>

          {promotionDraft && (
            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-600">
                  ТЗ: <span className="font-medium text-gray-900">{promotionDraft.task}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleCopyPromotionPart('title')}>
                    {copiedPromotionPart === 'title' ? 'Заголовок скопирован' : 'Скопировать заголовок'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleCopyPromotionPart('text')}>
                    {copiedPromotionPart === 'text' ? 'Текст скопирован' : 'Скопировать текст'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleCopyPromotionPart('all')}>
                    {copiedPromotionPart === 'all' ? 'Скопировано' : 'Скопировать всё'}
                  </Button>
                </div>
              </div>

              {promotionDraftCreatives.length > 0 ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">
                    Креативы {promotionDraftCreatives.length} шт. (900×480)
                  </p>
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    {promotionDraftCreatives.map((creative) => (
                      <div key={`${promotionDraft.contentItem.id}-${creative.index}-${creative.url}`} className="rounded-lg border border-gray-200 bg-white p-2 space-y-2">
                        <div className="w-full aspect-[15/8] overflow-hidden rounded-md border border-gray-100 bg-gray-50">
                          <img
                            src={creative.url}
                            alt={`Креатив для акции — вариант ${creative.index}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-gray-600">Вариант {creative.index}</span>
                          <a
                            href={creative.url}
                            download={`promotion-${promotionDraft.contentItem.id}-v${creative.index}-900x480.png`}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" />
                            Скачать
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Креатив не сгенерирован, но текст акции готов.
                </p>
              )}

              {promotionDraft.creativeWarning && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {promotionDraft.creativeWarning}
                </p>
              )}

              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-3">
                  {promotionDraft.contentItem.title}
                </h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {promotionDraft.contentItem.content}
                </p>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Generation form */}
      {activeType !== 'PROMOTION' && (
        <Card>
          <h2 className="text-base font-semibold text-gray-900 mb-1">
            Сгенерировать: {activeTypeConfig.label.toLowerCase()}
          </h2>
          <p className="text-sm text-gray-500 mb-4">{activeTypeConfig.description}</p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Бриф <span className="text-red-500">*</span>
              </label>
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder={activeTypeConfig.placeholder}
                rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>

            <Input
              label="Ключевые слова (необязательно, через запятую)"
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="стоматология, имплантация, Москва"
            />

            <Button
              variant="primary"
              size="lg"
              onClick={handleGenerate}
              disabled={isGenerating || !selectedBusinessId || !brief.trim()}
              loading={isGenerating}
              icon={!isGenerating ? <Sparkles className="w-4 h-4" /> : undefined}
            >
              {isGenerating ? 'Генерирую...' : 'Сгенерировать'}
            </Button>
          </div>
        </Card>
      )}

      {/* Content list */}
      <div className="space-y-3">
        <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide">
          История генераций — {activeTypeConfig.label} ({filteredItems.length})
        </h2>

        {isLoading && (
          <div className="text-center py-8 text-gray-400 text-sm">Загрузка...</div>
        )}

        {!isLoading && filteredItems.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-200">
            <Sparkles className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">Ещё нет сгенерированного контента этого типа</p>
          </div>
        )}

        {filteredItems.map((item) => (
          <Card key={item.id} padding="none" className="overflow-hidden">
            <div className="flex items-start justify-between p-4 gap-3">
              <div className="flex-1 min-w-0">
                {editingId === item.id ? (
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full border border-primary-300 rounded px-2 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                ) : (
                  <h3 className="text-sm font-semibold text-gray-900 leading-snug">{item.title}</h3>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-400">
                    {new Date(item.createdAt).toLocaleDateString('ru-RU', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-0.5 flex-shrink-0">
                {item.type === 'ARTICLE' && (
                  <Button
                    variant="ghost"
                    onClick={() => handlePublishToYandex(item)}
                    disabled={publishingId === item.id}
                    loading={publishingId === item.id}
                    icon={publishingId === item.id ? undefined : <Send className="w-4 h-4" />}
                    className="p-1.5 text-primary-600 hover:bg-primary-50 hover:text-primary-700"
                    title="Опубликовать в Яндекс Бизнес"
                  />
                )}
                {editingId === item.id ? (
                  <Button
                    variant="ghost"
                    onClick={() => handleEditSave(item.id)}
                    icon={<Check className="w-4 h-4" />}
                    className="p-1.5 text-green-600 hover:bg-green-50"
                    title="Сохранить"
                  />
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => handleEditStart(item)}
                    icon={<Edit2 className="w-4 h-4" />}
                    className="p-1.5 text-gray-400 hover:text-gray-600"
                    title="Редактировать"
                  />
                )}
                <Button
                  variant="ghost"
                  onClick={() => handleCopy(item)}
                  icon={copiedId === item.id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  className="p-1.5 text-gray-400 hover:text-gray-600"
                  title="Копировать всё"
                />
                <Button
                  variant="ghost"
                  onClick={() => handleDelete(item.id)}
                  icon={<Trash2 className="w-4 h-4" />}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50"
                  title="Удалить"
                />
                <Button
                  variant="ghost"
                  onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  icon={expandedId === item.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  className="p-1.5 text-gray-400 hover:text-gray-600"
                />
              </div>
            </div>

            {publishFeedback[item.id] && (
              <div
                className={`mx-4 mb-3 rounded-lg border px-3 py-2 text-sm ${
                  publishFeedback[item.id].type === 'success'
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : publishFeedback[item.id].type === 'warning'
                      ? 'border-amber-200 bg-amber-50 text-amber-800'
                      : 'border-red-200 bg-red-50 text-red-700'
                }`}
              >
                <p>{publishFeedback[item.id].message}</p>
                {publishFeedback[item.id].creativeUrl && (
                  <a
                    href={publishFeedback[item.id].creativeUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-1 underline text-xs"
                  >
                    Открыть сгенерированный креатив
                  </a>
                )}
              </div>
            )}

            {expandedId === item.id && (
              <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                {editingId === item.id ? (
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={12}
                    className="w-full border border-primary-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-vertical"
                  />
                ) : (
                  <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {item.content}
                  </div>
                )}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
